import { Button, message, Image } from "antd";
import { RedemptionItem } from "types/redemption";
import { useAddToCart } from "services/cart";
import { IMAGE_NOT_FOUND } from "constants/common";
import useUserStore from "stores/user";
import useLoginModal from "hooks/useLoginModal";
import NInputNumber from "components/Desktop/NInputNumber";
import { useState } from "react";
import useIsDesktopStore from "stores/isDesktop";
import { getImageFullPath } from "utils/image";

interface RedemptionItemCardProps {
  item: RedemptionItem;
  userCapsulePoints: number;
  onAddToCartSuccess?: () => void;
}

const RedemptionItemCard: React.FC<RedemptionItemCardProps> = ({
  item,
  userCapsulePoints,
  onAddToCartSuccess,
}) => {
  const { user } = useUserStore();
  const { loginModal, showLogin } = useLoginModal();
  const { addToCartAsync, isPending } = useAddToCart();
  const [productQty, setProductQty] = useState<number>(1);
  const { isDesktop } = useIsDesktopStore();

  const canRedeem = userCapsulePoints >= item.pointsCost * productQty;

  const handleOnChangeInput = (qty: number) => {
    setProductQty(qty);
  };

  const handleAddToCart = async () => {
    if (!user) {
      showLogin();
      return;
    }

    try {
      await addToCartAsync({
        redemptionItemId: item.id,
        redemptionType: item.type,
        quantity: productQty,
      });

      message.success(`เพิ่ม ${item.name} ลงตะกร้าแล้ว`);
      onAddToCartSuccess?.();
    } catch (error) {
      console.error("Failed to add redemption item to cart:", error);
      message.error("Failed to add item to cart. Please try again.");
    }
  };

  return (
    <>
      <div
        className="w-auto xl:w-[215px] p-2 sm:p-4 bg-white flex gap-3 flex-col relative product-card"
        style={{
          boxShadow: "1px 4px 13px 0px #7B2FE612",
          border: "1px solid #00000012",
        }}
      >
        {/* Main Content */}
        <div className="flex flex-col justify-between h-full">
          {/* Top Section */}
          <div>
            {/* Image */}
            <div className="text-center relative">
              <Image
                src={getImageFullPath(item.images?.[0] || "")}
                alt={item.name}
                width={isDesktop ? 180 : 160}
                height={isDesktop ? 180 : 160}
                className="object-contain"
                fallback={IMAGE_NOT_FOUND}
              />
              {/* Insufficient Points Overlay */}
              {/* {!canRedeem && (
                <div className="absolute inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center rounded-lg">
                  <span className="text-white font-semibold text-sm">
                    Insufficient Points
                  </span>
                </div>
              )} */}
            </div>

            <div className="text-base font-semibold mt-0 md:mt-2 line-clamp-2 break-words">
              {item.name}
            </div>

            {isDesktop && (
              <div className="text-gray-500 text-sm mt-1 line-clamp-2">
                <div className="flex justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-lg font-bold text-primary-500">
                      {(item.pointsCost * productQty).toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-600">คะแนน</span>
                  </div>
                  {item.type === "HYBRID" && (
                    <div className="text-sm font-medium text-gray-700">
                      + ฿{(item.cashCost * productQty).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <div>
            {/* Price and Quantity */}
            <div className="mt-4">
              {isDesktop ? (
                // Desktop Layout
                <div className="flex items-center justify-center">
                  {/* <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-bold text-primary-500">
                        {item.pointsCost * productQty}
                      </span>
                      <span className="text-xs text-gray-600">points</span>
                    </div>
                    {item.type === "HYBRID" && (
                      <div className="text-sm font-medium text-gray-700">
                        + ฿{item.cashCost * productQty}
                      </div>
                    )}
                  </div> */}
                  <NInputNumber
                    value={productQty}
                    onChange={handleOnChangeInput}
                    disabled={isPending}
                  />
                </div>
              ) : (
                // Mobile Layout
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-bold text-primary-500">
                        {(item.pointsCost * productQty).toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-600">คะแนน</span>
                    </div>
                    {item.type === "HYBRID" && (
                      <div className="text-sm font-medium text-gray-700">
                        + ฿{(item.cashCost * productQty).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-center">
                    <NInputNumber
                      value={productQty}
                      onChange={handleOnChangeInput}
                      disabled={isPending}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Button */}
            <Button
              type="primary"
              size="large"
              block
              loading={isPending}
              onClick={handleAddToCart}
              className={`mt-4 sm:mt-6 bg-primary-500 hover:bg-primary-600 border-primary-500`}
            >
              เพิ่มลงตะกร้า
            </Button>
          </div>
        </div>
      </div>

      {loginModal}
    </>
  );
};

export default RedemptionItemCard;
